import Phaser from 'phaser';
import { GAME_CONFIG } from '../config';
import { COLORS } from '../theme';

export type ConfettiTier = 'perfect' | 'good' | 'just';

interface TierConfig {
  count: number;
  durationMs: number;
  palette: number[];
}

// Tier-scaled bursts. PERFECT gets the full palette and ~60 pieces; JUST is a
// light sprinkle so the cheaper success still feels celebrated without
// overselling.
const TIERS: Record<ConfettiTier, TierConfig> = {
  perfect: {
    count: 60,
    durationMs: 2200,
    palette: [COLORS.yolk, COLORS.coral, COLORS.cream, COLORS.emerald, COLORS.rose],
  },
  good: {
    count: 36,
    durationMs: 1800,
    palette: [COLORS.yolk, COLORS.emerald, COLORS.cream],
  },
  just: {
    count: 18,
    durationMs: 1500,
    palette: [COLORS.cream, COLORS.yolk],
  },
};

/** Spawn a tier-scaled confetti burst that rains from above the canvas. Each
 * piece is a small rotated rectangle, drifts horizontally, fades out near the
 * end of its fall, and self-destroys. Fire-and-forget — no handle returned.
 *
 * Sits at depth 940 so it rains BEHIND the result popup (depth 950) but in
 * front of the character (depth 10) and scene effects (depths 2–5). */
export function spawnConfetti(scene: Phaser.Scene, tier: ConfettiTier) {
  const cfg = TIERS[tier];
  const { STAGE_W, STAGE_H } = GAME_CONFIG;
  for (let i = 0; i < cfg.count; i++) {
    const x = Phaser.Math.Between(20, STAGE_W - 20);
    const y = -20 - Math.random() * 80;
    const color = cfg.palette[i % cfg.palette.length];
    const w = Phaser.Math.Between(8, 14);
    const h = Phaser.Math.Between(4, 7);
    const piece = scene.add.rectangle(x, y, w, h, color).setOrigin(0.5).setDepth(940);
    piece.setRotation(Math.random() * Math.PI * 2);

    const drift = (Math.random() - 0.5) * 120;
    const spinDelta = (Math.random() - 0.5) * 14;
    const totalDur = cfg.durationMs + Math.random() * 400;

    scene.tweens.add({
      targets: piece,
      x: x + drift,
      y: STAGE_H + 40,
      rotation: piece.rotation + spinDelta,
      duration: totalDur,
      ease: 'Cubic.In',
      delay: Math.random() * 200,
    });
    scene.tweens.add({
      targets: piece,
      alpha: 0,
      duration: 400,
      delay: totalDur - 300,
      onComplete: () => piece.destroy(),
    });
  }
}
